// data/zones/zone5.ts — THE GILDED VAULT.
// DATA ONLY. Numbers, tables, ASCII maps. No behaviour lives here — the zone's
// mechanic is content/zones/zone5.ts, the fight is content/bosses/boss5.ts, and
// the systems that read this are game/*.
//
// DESIGN §5 Zone 5: "wealth is a trap — this zone sells you everything, gives
// you nothing, and punishes the grab you didn't appraise."
//
// ---------------------------------------------------------------------------
// GLYPH VOCABULARY — the three tiles this zone adds to the shared legend.
// game/rooms.ts leaves every glyph it does not recognise as plain floor, so all
// three are furniture the zone mechanic builds at room start from
// ctx.findGlyphTiles(). None of them is solid: they are drawn, not collided
// with (RunCtx has no set-solid API and shared code is not ours to change).
//
//   'T' — treasure pile. Rolled clean or CURSED at room start (seeded).
//   'X' — coffer. The zone's shop: E buys food, or a potion when you are whole.
//   'H' — chest. One in four is a Mimic; a chest either breathes or it does not,
//         and that is the tell.
//
// 'C' (the shared "chest" glyph, which is really just a fat gold pile) is left
// alone here. The Gilded Vault's chests are 'H', because a chest you can open is
// a decision and a gold pile is not.
// ---------------------------------------------------------------------------

import type {
  EnemyDef,
  GeneratorDef,
  ProjectileDef,
  RoomTemplate,
  ZoneConfig,
  ZonePalette,
} from '../../types'
import { ZONE3_ENEMIES } from './zone3'

// ---------------------------------------------------------------- palette
// The hold's treasury. Deep gold floors under tarnished brass walls, and a
// black-red shadow behind everything so the gold reads as *hoarded* rather than
// as daylight. `torches: 0.1` — the only zone besides the Broken Gate with fire
// in its walls, because opulence lights itself, and sparsely.

export const ZONE5_PALETTE: ZonePalette = {
  // The floor is deliberately several stops DARKER than the walls and than
  // everything that stands on it. Almost every body, pickup and hazard in this
  // zone is gold; if the ground were gold too, a Coinling would be invisible at
  // ten paces. Dark tarnish under bright brass is what keeps the zone readable
  // while still reading, at a glance, as a treasury.
  floor: '#382a12',
  floorAlt: '#42331a',
  wall: '#7a5a24',
  wallEdge: '#c39a3a',
  accent: '#ffd166',
  glow: '#ffe08a',
  bg: '#120806',
  torches: 0.1,
}

/** Colours the mechanic and the fight draw with. Palette-adjacent, not tiles. */
export const ZONE5_COLORS = {
  gold: '#e3b23c',
  goldHot: '#ffe9a8',
  goldDeep: '#8a6320',
  brass: '#c39a3a',
  clean: '#fff2c4',
  cursed: '#ff4a4a',
  cursedDeep: '#8a1f1f',
  shadow: '#1a0d08',
  plate: '#d8c07a',
  coin: '#ffd166',
} as const

// ---------------------------------------------------------------- the rules
// Every literal the economy inversion reads. One block, because the whole zone
// is one argument about money.

export const ZONE5_RULES = {
  // --- TOLL DOORS (DESIGN §5: "each room's exit demands gold: ~30/60/90")
  toll: {
    /** Price by combat-room index. Rooms past the table pay the last entry. */
    byRoom: [30, 60, 90] as number[],
    /** Push applied to a Warden who cannot pay, so vetoing is not a grind. */
    shove: 240,
    /** Seconds between "you cannot afford this" barks. */
    barkGap: 2.2,

    /**
     * THE DEBT — and the reason this zone cannot softlock a run.
     *
     * A cleared room stops filling the Gloom (game/gloom.ts), gloomspawn pay
     * nothing (data/zones/zone1.ts, D-012), and a stripped room has no other
     * income. So a Warden who spent their last coin at a coffer and then found a
     * 90-gold door would be stuck in a quiet room forever, which is precisely
     * the unrecoverable state the directive forbids.
     *
     * So the door will not hold you: shove through it a second time and it takes
     * every coin you are carrying and puts the shortfall on the Gloom instead —
     * a quarter of the meter, carried for the rest of the zone. Nobody who can
     * pay would ever choose that, and nobody who cannot is trapped by it.
     */
    debtGloom: 0.25,
    /**
     * Seconds of leaning on a door that has already refused you before it opens
     * on those terms. Long enough that nobody pays it by accident: the refusal
     * bark says what happens next, and three full seconds of holding into the
     * door after reading it is a decision.
     */
    debtDelay: 3,
  },

  /**
   * THE FRUGAL DEED — data/feats.ts `z5_frugal` ("Appraiser", track 'z5Frugal',
   * desc: "Clear the Gilded Vault having spent under 100 gold").
   *
   * FLAGGED CONFLICT, and the reason `countTolls` exists. The feat's threshold
   * lives nowhere in shared code — feats.ts carries `target: 1`, so the deed
   * completes on one tick and the number 100 exists only in its description.
   * Three toll doors at DESIGN's own 30/60/90 cost 180 gold that no player can
   * refuse: counting tolls makes the deed arithmetically impossible, and a deed
   * that can never tick is worse than a deed whose wording is a shade loose.
   *
   * So the deed measures what you CHOSE to buy — the coffers — and the tolls
   * are the tax you were always going to pay. "Appraiser" is exactly that
   * claim: you read the piles well enough that you never needed the shop.
   *
   * ORCHESTRATOR: flipping `countTolls` to true makes the wording literal and
   * requires a toll table summing under 100 (e.g. [20, 30, 45]). That trade
   * costs the zone most of its economic pressure, which is why it is not the
   * default. Both halves of the switch are here so the call is one line.
   */
  frugal: {
    limit: 100,
    countTolls: false,
  },

  // --- CURSED TREASURE
  treasure: {
    /** Seeded chance a 'T' pile is cursed (DESIGN: the tell is always visible). */
    cursedChance: 0.4,
    cleanGold: [25, 40] as [number, number],
    cursedGold: [60, 90] as [number, number],
    /** DESIGN §5 Gloom: "grabbing cursed treasure +8% each. Greed darkens." */
    cursedGloom: 0.08,
    /** Of the two consequences, how often the grab pours an ambush. */
    ambushChance: 0.6,
    ambush: {
      min: 2,
      max: 4,
      /** Floor markers before the bodies land (readability law: >= 0.4s). */
      telegraph: 0.6,
      minRadius: 150,
      maxRadius: 240,
      /** Bodies the pour draws from. */
      defs: ['coinling', 'gilded_courtier', 'reliquary_warden'] as string[],
    },
    mark: {
      seconds: 10,
      /** DESIGN §5: "enemies +15% speed toward you". */
      speedMul: 1.15,
    },
  },

  // --- COFFERS ('X')
  coffer: {
    foodCost: 30,
    potionCost: 55,
    /** Purchases per coffer. Infinite HP for gold would eat the zone's teeth. */
    uses: 2,
    reach: 54,
  },

  // --- CHESTS ('H') and the Mimics inside a quarter of them
  chest: {
    mimicChance: 0.25,
    gold: [40, 70] as [number, number],
    reach: 54,
    /** The eruption's tell, before the body exists. */
    telegraph: 0.5,
    /** A Mimic taken down is the fattest purse in the room. */
    mimicGold: [60, 90] as [number, number],
    /** Breath cycle of the tell, in seconds. */
    breathPeriod: 1.6,
  },

  // --- COINLING LOOT (DESIGN §5: "die into grabbable gold that fades in 3s")
  coin: {
    min: 3,
    max: 5,
    value: 4,
    seconds: 3,
    /** Scatter radius of the burst. */
    spread: 34,
    /** How close the Warden must be to sweep one up. */
    reach: 22,
    /** Hard cap on live motes, so a swarm wipe cannot flood the frame. */
    cap: 64,
  },

  /** The zone's only free food: the Counting Room carries this many rations on
   *  top of the one game/rooms.ts stocks every timed vault with. */
  countingRoomFood: 1,
} as const

// ---------------------------------------------------------------- projectiles

export const ZONE5_PROJECTILES: ProjectileDef[] = [
  // Readability law: a straight shot that happens to survive one wall, so it
  // stays white-gold. The bounce is the behaviour to learn — the disc that
  // missed you is not spent yet.
  {
    id: 'relicdisc',
    kind: 'ricochet',
    speed: 250,
    damage: 7,
    radius: 8,
    color: '#ffe9a8',
    lifetime: 4.2,
    bounces: 1,
  },
  // The Hoard's coin-spray (P2+). Same family, cheaper per disc, wider fan.
  {
    id: 'coinspray',
    kind: 'ricochet',
    speed: 232,
    damage: 6,
    radius: 7,
    color: '#ffd98a',
    lifetime: 3.4,
    bounces: 1,
  },
]

// ---------------------------------------------------------------- enemies

/**
 * DESIGN §5 Zone 5: "Courtier Blades return, gilded, husk-less (they learned
 * nothing)." So it is literally the Pale Court body — same stats, same two-step
 * lunge brain, same red glint — wearing gold leaf. The reuse is the joke and
 * the statement: the court that could not stay dead is now just staff. There is
 * no husk system in this zone, so nothing they do here sticks.
 */
const COURTIER: EnemyDef = ZONE3_ENEMIES.find((e) => e.id === 'courtier_blade') as EnemyDef

export const ZONE5_ENEMIES: EnemyDef[] = [
  {
    // TANK. Animated armour behind a tower shield: immune from the front, open
    // from the flank, and open outright through its swing and its long recovery.
    // The Shellback's device (zone 2) with a heavier hand and a slower neck —
    // 1.6 rad/s of turn is comfortably slower than a Warden walking a circle.
    id: 'hollow_plate',
    name: 'Hollow Plate',
    role: 'tank',
    hp: 44,
    speed: 62,
    radius: 14,
    touchDamage: 8,
    ai: {
      custom: 'z5-plate',
      frontalArcDeg: 140,
      turnRate: 1.6,
      lungeRange: 118,
      lungeCooldown: 3.2,
      windup: 0.62,
      lungeSpeed: 430,
      lungeTime: 0.3,
      /** the shield-bash multiplier on touchDamage */
      snapDamage: 1.7,
      recover: 1.35,
    },
    sprite: { key: 'z5_plate', size: 34, params: { steel: '#6d6a63', gilt: '#e3b23c', dark: '#241a12' } },
    emberValue: 5,
    goldChance: 0.42,
  },
  {
    // SWARM. A cluster of animated coin that scuttles. It pays nothing through
    // the ordinary loot roll (goldChance 0) — it bursts into motes that fade in
    // three seconds, and diving for them is the exposure. That is the whole
    // body: risk-bait with legs.
    id: 'coinling',
    name: 'Coinling',
    role: 'swarm',
    hp: 11,
    speed: 136,
    radius: 9,
    touchDamage: 5,
    ai: {
      custom: 'z5-coinling',
      lungeRange: 92,
      lungeCooldown: 1.7,
      windup: 0.24,
      lungeSpeed: 380,
      lungeTime: 0.22,
    },
    sprite: { key: 'z5_coinling', size: 22, params: { gold: '#e3b23c', hot: '#ffe9a8', eye: '#ff4a4a' } },
    emberValue: 1,
    goldChance: 0,
  },
  {
    // RANGED+. Hurls relic-discs that survive one wall. The counterplay is no
    // longer "stand behind cover" — cover is where the second pass comes from.
    id: 'reliquary_warden',
    name: 'Reliquary Warden',
    role: 'ranged',
    hp: 30,
    speed: 76,
    radius: 12,
    touchDamage: 6,
    ai: {
      custom: 'z5-warden',
      keepRange: 262,
      minRange: 168,
      fireRange: 344,
      fireCooldown: 3.4,
      windup: 0.52,
      /** discs per volley, fanned */
      discs: 2,
      fanDeg: 14,
      projectile: 'relicdisc',
    },
    sprite: { key: 'z5_warden', size: 32, params: { robe: '#5a3a52', gilt: '#e3b23c', bone: '#e8e0c8' } },
    emberValue: 4,
    goldChance: 0.4,
  },
  {
    // STALKER. It only exists because you opened it. Lunge-bites on a short
    // leash, tanky, and worth more gold than any honest chest in the room.
    id: 'mimic',
    name: 'Mimic',
    role: 'stalker',
    hp: 62,
    speed: 104,
    radius: 14,
    touchDamage: 11,
    ai: {
      custom: 'z5-mimic',
      lungeRange: 150,
      lungeCooldown: 2.2,
      windup: 0.42,
      lungeSpeed: 470,
      lungeTime: 0.26,
      biteDamage: 1.4,
      biteRange: 46,
      recover: 0.6,
    },
    sprite: { key: 'z5_mimic', size: 34, params: { wood: '#5c3a1c', gilt: '#e3b23c', maw: '#8a1f1f' } },
    emberValue: 6,
    goldChance: 0,
  },
  {
    ...COURTIER,
    id: 'gilded_courtier',
    name: 'Gilded Courtier',
    sprite: { key: 'z5_courtier', size: 32, params: { cloth: '#4a2f1a', gilt: '#e3b23c', glint: '#ff4a4a' } },
    goldChance: 0.44,
  },
]

// ---------------------------------------------------------------- generators

export const ZONE5_GENERATORS: GeneratorDef[] = [
  {
    id: 'gen_treasureheap',
    name: 'Treasure Heap',
    hp: 68,
    radius: 16,
    spawns: 'coinling',
    interval: 3.4,
    burst: 2,
    cap: 6,
    sprite: { key: 'z5_gen_heap', size: 42, params: { gold: '#e3b23c' } },
    emberValue: 4,
  },
  {
    id: 'gen_armoryrack',
    name: 'Armory Rack',
    hp: 104,
    radius: 16,
    spawns: 'hollow_plate',
    interval: 7.2,
    burst: 1,
    cap: 2,
    sprite: { key: 'z5_gen_rack', size: 44, params: { steel: '#6d6a63', gilt: '#e3b23c' } },
    emberValue: 6,
  },
  {
    id: 'gen_reliquaryalcove',
    name: 'Reliquary Alcove',
    hp: 86,
    radius: 16,
    spawns: 'reliquary_warden',
    interval: 6.2,
    burst: 1,
    cap: 3,
    sprite: { key: 'z5_gen_alcove', size: 42, params: { gilt: '#e3b23c', bone: '#e8e0c8' } },
    emberValue: 5,
  },
]

// ---------------------------------------------------------------- rooms
// 44x26 at 32px, the same frame as zones 1 and 3 so only the rules differ.
// Six combat templates in three ordered groups; game/zones.ts cuts the library
// into roomCount contiguous groups and draws one from each, so a run through the
// Gilded Vault always reads:
//
//   group 1 (1 source):  z5-coindrifts, z5-tallyhall
//     — coinlings, loose treasure, the first toll. Learn to appraise and pay.
//   group 2 (2 sources): z5-countinghouse*, z5-armoury
//     — Hollow Plates, Reliquary Wardens, and the first chests. Learn to look.
//   group 3 (3 sources): z5-strongroom*, z5-greathoard
//     — everything at once, one source behind iron with the key on an elite.
//   (* carries the timed vault — the COUNTING ROOM)
//
// The respite (z5-tallychapel) sits in the same array and is never a combat
// pick: the runner recognises it by its altar glyph.

export const ZONE5_ROOMS: RoomTemplate[] = [
  // The Coin Drifts — the first room of the zone and its whole thesis in one
  // hall: drifts of coin between low walls, four piles you have to read, a
  // coffer against the west wall, and a door that wants thirty gold on the way
  // out.
  {
    id: 'z5-coindrifts',
    generators: ['gen_treasureheap'],
    patrols: ['coinling', 'gilded_courtier'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@.......................................#',
      '#..........................................#',
      '#....####........T..........####...........#',
      '#....####...................####...........#',
      '#..........................................#',
      '#..........E...............................#',
      '#...............$..........................#',
      '#....########..................########....#',
      '#..........................................#',
      'D...................G......................D',
      '#..........................................#',
      '#....########..................########....#',
      '#..........T...............................#',
      '#....................E.....................#',
      '#..........................................#',
      '#....####..................####............#',
      '#....####..................####.......T....#',
      '#..........................................#',
      '#.........X................................#',
      '#..........................................#',
      '#..............T...........E...............#',
      '#..........................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Tally Hall — rows of counting benches. The heap sits in the far corner
  // so the coinlings arrive down the aisles, and the piles are spaced so that
  // taking two in a row means crossing the room with a swarm behind you.
  {
    id: 'z5-tallyhall',
    generators: ['gen_treasureheap'],
    patrols: ['gilded_courtier', 'coinling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@..............T........................#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..........................................#',
      '#...........E..............................#',
      '#.....................$....................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..........................................#',
      'D.........T................................D',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..................E.......................#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#....X.....................................#',
      '#.......................T..................#',
      '#..........................E...........G...#',
      '#..........................................#',
      '#...........T..............................#',
      '#####################D######################',
    ],
  },
  // The Counting House — the zone's timed vault, and therefore the COUNTING
  // ROOM (DESIGN §5): free entry under the clock, clean gold and the only free
  // food in the Gilded Vault behind a golden door in the north-east. Two
  // sources — an armoury rack and a reliquary alcove — so the room teaches the
  // Plate's flank and the Warden's ricochet in the same breath.
  {
    id: 'z5-countinghouse',
    generators: ['gen_armoryrack', 'gen_reliquaryalcove'],
    patrols: ['hollow_plate', 'reliquary_warden', 'coinling'],
    map: [
      '############################################',
      '#...........................#vvvvvvvv#.....#',
      '#...........................Vvvvvvvvv#.....#',
      '#..@........................Vvvvvvvvv#.....#',
      '#...........................#vvvvvvvv#.....#',
      '#...........................##########.....#',
      '#.....####.................................#',
      '#.....####........H................E.......#',
      '#.....####.................................#',
      '#..........................................#',
      '#..........G...............................#',
      '#..........................................#',
      'D.......########..............########.....#',
      '#..........................................#',
      '#.......T..................................#',
      '#..............E..................T........#',
      '#..........................$...............#',
      '#..........X...............................#',
      '#....####..................................#',
      '#....####...........E...........H..........#',
      '#....####..................................#',
      '#..........................................#',
      '#....................T.............G.......#',
      '#..........................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Armoury — plate racks in rows with an alcove at each end. Sightlines
  // are short and the discs come off the walls, so the room's lesson is that
  // cover in the Gilded Vault is a place shots come back from.
  {
    id: 'z5-armoury',
    generators: ['gen_treasureheap', 'gen_armoryrack'],
    patrols: ['hollow_plate', 'coinling', 'reliquary_warden'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@.......................................#',
      '#..........................................#',
      '#....######........T.........######........#',
      '#....######..................######........#',
      '#..........................................#',
      '#............E.............................#',
      '#.........H................................#',
      '#..........................................#',
      '#....####.......................####.......#',
      'D....####...........G...........####.......D',
      '#....####.......................####.......#',
      '#..........................................#',
      '#..................T.......................#',
      '#..........X..................E............#',
      '#..........................................#',
      '#....######.......................H........#',
      '#....######................######..........#',
      '#..........................######..........#',
      '#..........$...............................#',
      '#.......T.........E........................#',
      '#..........................................#',
      '#..............G...................T.......#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Strongroom — three sources, the last behind iron, and the key walks the
  // room on a marked elite. The vault sits in the south-west so the fast route
  // and the safe route disagree, which is the whole point of a timed door.
  {
    id: 'z5-strongroom',
    generators: ['gen_armoryrack', 'gen_treasureheap', 'gen_reliquaryalcove'],
    patrols: ['hollow_plate', 'reliquary_warden', 'coinling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@.......................................#',
      '#..........T............E..................#',
      '#...........................################',
      '#...........................#..............#',
      '#.......###.................#..............#',
      '#.......###.................#.......G......#',
      '#.......###.........K.......LLL............#',
      '#...........................#..............#',
      '#..........H................#....$.....H...#',
      '#................####.......#..............#',
      '#.....E..........####.......################',
      '#................####......................#',
      '#...........G.....................T........#',
      '#........................E.................#',
      '#...X......................................#',
      '#.......###.....#..........................#',
      '#....########...#..........................#',
      '#....#vvvvvv#...#..........................#',
      '#....#vvvvvvV..............T...............#',
      '#....#vvvvvvV.........G....................#',
      '#....########..............................#',
      '#..........................................#',
      '#.......T..................................#',
      '#####################D######################',
    ],
  },
  // The Great Hoard — the widest room in the zone and the last thing before the
  // chapel. Three sources at three points, two coffers because by now you are
  // buying health rather than finding it, and a gate across the south-east.
  {
    id: 'z5-greathoard',
    generators: ['gen_treasureheap', 'gen_armoryrack', 'gen_reliquaryalcove'],
    patrols: ['hollow_plate', 'reliquary_warden', 'gilded_courtier'],
    map: [
      '############################################',
      '#..........................................#',
      '#.....G.............E................G.....#',
      '#..........................................#',
      '#....##.....................##.....##......#',
      '#....##.....................##.....##......#',
      '#..........................................#',
      '#........T.........................T.......#',
      '#..........................................#',
      '#........########........########..........#',
      '#..........................................#',
      'D.............E...................E........D',
      '#..........................................#',
      '#....H.......####......####...........H....#',
      '#............####......####................#',
      '#..........................................#',
      '#....$...............E...............$.....#',
      '#...............K..........................#',
      '#........########........########..........#',
      '#..........................#################',
      '#....##....................#...............#',
      '#....##....................L....G..........#',
      '#....X.....................L...............#',
      '#..@.........T.............L.......T.......#',
      '#.......X..................#......$........#',
      '#####################D######################',
    ],
  },
  // ------------------------------------------------------------- the respite
  // The Tally Chapel: a counting-house chapel where the ledgers are the altar
  // rail. The Altar takes gold — and this is the zone where you have gold, so
  // the mercy here is real, and it is the design's balance for a zone that
  // sells you your own health. Nothing spawns; the Gloom stops filling; the
  // chapel's exits are toll-free, because it is not selling you anything you
  // have not already paid for.
  {
    id: 'z5-tallychapel',
    generators: [],
    patrols: [],
    map: [
      '#################S################',
      '#................................#',
      '#................................#',
      '#....##....................##....#',
      '#....##....................##....#',
      '#................................#',
      '#.......A.................N......#',
      '#................................#',
      '#....####..............####......#',
      '#....####..............####......#',
      '#................................#',
      '#................................#',
      '#..............@.................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '##################################',
    ],
  },
]

// ============================================================================
// BOSS 5 — THE HOARD (DESIGN §5 Zone 5)
// "Verb: deny it food — your loot is its healing."
// Every literal the fight reads lives here; content/bosses/boss5.ts is behaviour.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// The treasury floor. 44x21 to match the framing pass (D-016) so the whole
// serpent fits on screen. Four low coin-drifts break the middle so the coils
// have something to wind around; everything else is open, because a serpent you
// cannot see coming is not a fight, it is an ambush.
export const ZONE5_BOSS_ARENA: RoomTemplate = {
  id: 'z5-treasury',
  generators: [],
  patrols: [],
  map: [
    '############################################',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.......####......................####.....#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.......####......................####.....#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.....................@....................#',
    '#..........................................#',
    '#..........................................#',
    '######################D#####################',
  ],
}

/**
 * Boss tuning block. Per-phase arrays are indexed phase-1 (P1, P2, P3).
 *
 * FLAGGED INTERPRETATION (`eat.healFraction`): DESIGN §5 says a pile eaten is
 * "+4% HP heal + a temporary gilded armor plate". It is a HEAL of 4% of max HP,
 * not growth of the bar — a boss whose maximum rises would retroactively devalue
 * every point of damage already dealt, which is a different (and much nastier)
 * mechanic than the one the design record describes. The armour is the half of
 * the meal you feel immediately; the heal is the half you feel at the end.
 *
 * FLAGGED INTERPRETATION (the hittable head): every shared hit path in the game
 * tests exactly one circle — `boss.pos` against `boss.radius` (game/game.ts
 * hitBoss, game/weapons.ts inArc). Content cannot add hitboxes without editing
 * shared code, so THE HEAD IS THE FIGHT: the segments are hazard, not target.
 * That is a design position as much as a limitation — you have to face the
 * biting end to hurt it, and the coils exist to make facing it expensive.
 */
export const ZONE5_BOSS = {
  id: 'hoard',
  name: 'THE HOARD',

  hp: 1000,
  /** Head radius: generous, because the head is the only target — and matched
   *  to the baked art (the head sprite is 60px wide; a 26px circle left the
   *  crown and jaw edges whiffing shots that visually connected — D-032). */
  radius: 30,
  /** Tile coordinates inside ZONE5_BOSS_ARENA. */
  startTile: { tx: 22, ty: 6 },
  /** DESIGN §5: P2 at 60%, P3 at 25%. */
  phases: [0.6, 0.25] as number[],

  /** The body: a head and a train of segments that follow its own path. */
  body: {
    segments: 10,
    /** px each coil holds off the one ahead — under 2x radius, so it reads as
     *  one continuous body rather than a string of beads. */
    spacing: 17,
    radius: 15,
    /** contact damage from a coil, and how often a coil can bite you */
    touchDamage: 11,
    touchGap: 0.8,
  },

  /** Cruise: smooth arcs, pile to pile. Faster once it is hungry (P2/P3). */
  move: {
    speed: [92, 124, 132] as number[],
    /** rad/s the head may turn — the arcs are the tell that it is not a dodger */
    turnRate: [2.1, 2.6, 2.9] as number[],
  },

  /** The lunge-bite. Red glint, then a committed dash. */
  bite: {
    range: 128,
    interval: [3.4, 2.7, 2.4] as number[],
    windup: [0.55, 0.46, 0.42] as number[],
    dashSpeed: 430,
    dashTime: 0.32,
    damage: 17,
    /** how close the head has to end up to actually land the bite */
    hitRange: 62,
    recover: 0.7,
  },

  /** THE MEAL. This is the fight. */
  eat: {
    /** How close the head must be to start swallowing. */
    reach: 40,
    /** DESIGN §5: a telegraphed gulp, not a passive drain. */
    gulpSeconds: 1.2,
    /** Fraction of MAX hp restored per pile. */
    healFraction: 0.04,
    /** Hits that will be turned aside by the plate this meal buys. */
    plateHits: 3,
    /** Ceiling on stacked plated hits, so a fed Hoard is never unkillable. */
    plateCap: 9,
    /** Damage a plated hit lets through (DESIGN: "next 3 hits do 1 dmg"). */
    platedDamage: 1,
    /** D-032: a meal is contestable by force. This many hits landed on the
     *  head mid-swallow — or this much raw (pre-plate) damage — snaps the jaw
     *  shut on nothing. Both thresholds exist so every form can contest: the
     *  bow reaches 3 hits, the maul reaches 25 raw in one slam. */
    interruptHits: 3,
    interruptDamage: 25,
  },

  /** THE FLOOR. Ten piles at the start (DESIGN §5: "littered with gold piles"). */
  piles: {
    gold: 45,
    radius: 18,
    /** Authored tiles inside ZONE5_BOSS_ARENA — spread, never on furniture. */
    tiles: [
      { tx: 5, ty: 3 },
      { tx: 16, ty: 2 },
      { tx: 27, ty: 2 },
      { tx: 38, ty: 3 },
      { tx: 4, ty: 10 },
      { tx: 21, ty: 9 },
      { tx: 39, ty: 10 },
      { tx: 6, ty: 17 },
      { tx: 22, ty: 15 },
      { tx: 37, ty: 17 },
    ] as { tx: number; ty: number }[],
  },

  /** The Warden's answer: a vulnerable channel, and why greed has timing.
   *  D-032: 0.6s/16px read as brittle in human hands — the commitment stays,
   *  the stickiness goes. */
  grab: {
    reach: 46,
    seconds: 0.45,
    /** px of drift allowed before the channel breaks */
    moveSlack: 26,
  },

  /** Coinling pairs. Their deaths become floor gold: the fight feeds itself. */
  summon: {
    interval: [8.5, 6.5, 7.5] as number[],
    count: 2,
    liveCap: 8,
    defId: 'coinling',
    spread: 150,
    /** Gold a dead escort leaves on the floor as a fresh pile. */
    pileGold: 20,
  },

  /** P2: the escort that makes the arena expensive to cross. */
  escort: {
    defId: 'hollow_plate',
    count: 1,
    /** re-sent this often if it is dead and the Hoard is still in P2+ */
    interval: 16,
  },

  /** P2: coin-spray breath — a fan of ricochets down the lane it is facing. */
  spray: {
    interval: [0, 5.4, 4.6] as number[],
    windup: 0.5,
    count: 7,
    spreadDeg: 74,
    projectile: 'coinspray',
  },

  /** P3: the chandeliers. Three of them, once, and each one is a heal window. */
  chandeliers: {
    count: 3,
    telegraph: 1.5,
    /** seconds between drops */
    gap: 4.5,
    crashDamage: 24,
    crashRadius: 66,
    /** A chandelier is worth more than any floor pile — that is why it rushes. */
    gold: 120,
    /** Authored fall points (tiles). One per chandelier. */
    tiles: [
      { tx: 11, ty: 6 },
      { tx: 32, ty: 6 },
      { tx: 22, ty: 13 },
    ] as { tx: number; ty: number }[],
  },

  /** P3: the coil sweep — half the arena at once, dashable. */
  sweep: {
    interval: 8.0,
    telegraph: 1.1,
    seconds: 0.85,
    damage: 20,
    push: 260,
  },

  intro: { shake: 7, shakeSeconds: 0.8 },
  death: { shake: 13, shakeSeconds: 1.2, hitstop: 0.3 },
} as const

// ---------------------------------------------------------------- zone

export const ZONE5: ZoneConfig = {
  id: 5,
  name: 'The Gilded Vault',
  tagline: 'Everything here is for sale. Nothing here is free.',
  palette: ZONE5_PALETTE,
  rooms: ZONE5_ROOMS,
  roomCount: 3,
  gloomFillSeconds: 85,
  vaultClockSeconds: 150,
  eliteRate: 0.12,
  eliteRiders: ['frenzied', 'shielded', 'bloated'],
  statScale: { hp: 1.57, damage: 1.46 },
  // THE ECONOMY INVERSION, in one line of data: no floor food anywhere in this
  // zone. Health is bought at a coffer or at the altar, or it is not had.
  foodPerRoom: [0, 0],
  music: {
    root: 44,
    mode: 'harmonicMinor',
    tempo: 72,
    padLevel: 0.85,
    bassLevel: 0.9,
    percLevel: 0.5,
    character: 'gilded',
  },
  bossId: 'hoard',
}
